import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageCircle, Users, FileText, BarChart2, Plus, Trash2,
  Copy, Send, Loader2, CheckCircle, XCircle, Clock, AlertCircle,
  Search, Upload, X, Check, ChevronLeft, ChevronRight, RefreshCw,
  Eye, Phone, User, Tag, ToggleLeft, ToggleRight, Zap, Activity,
  Settings, Link2, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';

// ── Theme constants ────────────────────────────────────────────────────────
const WA_GREEN  = '#25D366';
const WA_DARK   = '#128C7E';
const WA_LIGHT  = '#DCF8C6';

// ── Status badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    sent:       'bg-emerald-100 text-emerald-700',
    sending:    'bg-blue-100 text-blue-700',
    queued:     'bg-blue-100 text-blue-600',
    draft:      'bg-slate-100 text-slate-600',
    scheduled:  'bg-amber-100 text-amber-700',
    failed:     'bg-red-100 text-red-600',
    demo:       'bg-violet-100 text-violet-600',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full capitalize ${map[status] || 'bg-slate-100 text-slate-500'}`}>
      {status === 'sent' && <Check className="w-3 h-3" />}
      {status === 'sending' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status === 'failed' && <XCircle className="w-3 h-3" />}
      {status === 'scheduled' && <Clock className="w-3 h-3" />}
      {status}
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function WhatsAppPage() {
  const [tab, setTab] = useState('campaigns');
  const [apiStatus, setApiStatus] = useState(null);

  useEffect(() => {
    api.get('/whatsapp/status').then(r => setApiStatus(r)).catch(() => {});
  }, []);

  const tabs = [
    { id: 'campaigns', icon: Send,        label: 'Campaigns' },
    { id: 'templates', icon: FileText,    label: 'Templates' },
    { id: 'contacts',  icon: Users,       label: 'Contacts'  },
    { id: 'analytics', icon: BarChart2,   label: 'Analytics' },
  ];

  return (
    <div className="px-6 md:px-8 py-6 md:py-8">
      {/* Header */}
      <div className="relative rounded-3xl overflow-hidden mb-8"
        style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)', minHeight: 130 }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-7">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: WA_GREEN }}>
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-white tracking-tight">WhatsApp Messaging</h1>
              <p className="text-emerald-200 text-sm mt-0.5">Send campaigns via WhatsApp Business Cloud API</p>
            </div>
          </div>
          {apiStatus && (
            <div className="flex items-center gap-2">
              {apiStatus.configured
                ? <span className="flex items-center gap-1.5 bg-emerald-900/40 border border-emerald-600/40 text-emerald-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live — Meta Connected
                  </span>
                : <span className="flex items-center gap-1.5 bg-amber-900/40 border border-amber-600/40 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    Demo Mode
                  </span>
              }
            </div>
          )}
        </div>
      </div>

      {/* Setup Banner */}
      {apiStatus && !apiStatus.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="wa-setup-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800 mb-1">WhatsApp not connected — running in Demo Mode</p>
            <p className="text-amber-700 text-xs">
              Add <strong>META_ACCESS_TOKEN</strong>, <strong>META_PHONE_NUMBER_ID</strong>, and <strong>META_WABA_ID</strong> to your backend <code>.env</code> to activate live sending.
              <br />Webhook URL for Meta: <code className="bg-amber-100 px-1 rounded">{apiStatus.webhook_url}</code>
              &nbsp;| Verify token: <code className="bg-amber-100 px-1 rounded">{apiStatus.verify_token}</code>
            </p>
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl w-fit mb-8" data-testid="wa-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`wa-tab-${t.id}`}>
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'campaigns' && <CampaignsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'contacts'  && <ContactsTab />}
      {tab === 'analytics' && <AnalyticsTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CAMPAIGNS TAB
// ══════════════════════════════════════════════════════════════════════════════
function CampaignsTab() {
  const [campaigns, setCampaigns]         = useState([]);
  const [templates, setTemplates]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalPages, setTotalPages]       = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [createModal, setCreateModal]     = useState(false);
  const [detailModal, setDetailModal]     = useState(null);
  const [sending, setSending]             = useState({});
  const [form, setForm] = useState({ name: '', template_name: '', template_language: 'en_US', scheduled_at: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, tr] = await Promise.all([
        api.get(`/whatsapp/campaigns?page=${currentPage}&limit=10`),
        api.get('/whatsapp/templates'),
      ]);
      setCampaigns(cr.data || []);
      setTotalPages(cr.total_pages || 1);
      setTotalCampaigns(cr.total || 0);
      setTemplates(tr.data || []);
    } finally { setLoading(false); }
  }, [currentPage]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.template_name) { toast.error('Name and template are required'); return; }
    try {
      await api.post('/whatsapp/campaigns', form);
      toast.success('Campaign created');
      setCreateModal(false);
      setForm({ name: '', template_name: '', template_language: 'en_US', scheduled_at: '' });
      load();
    } catch (err) { toast.error(err?.detail || 'Failed to create'); }
  };

  const handleSend = async (c) => {
    setSending(s => ({ ...s, [c.id]: true }));
    try {
      const r = await api.post(`/whatsapp/campaigns/${c.id}/send`);
      toast.success(r.message || 'Campaign queued!');
      setTimeout(load, 1500);
    } catch (err) { toast.error(err?.detail || 'Send failed'); }
    finally { setSending(s => ({ ...s, [c.id]: false })); }
  };

  const handleDuplicate = async (c) => {
    try {
      await api.post(`/whatsapp/campaigns/${c.id}/duplicate`);
      toast.success('Campaign duplicated');
      load();
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/whatsapp/campaigns/${c.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Campaigns</h2>
          <p className="text-sm text-slate-500 mt-0.5">{totalCampaigns} campaign{totalCampaigns !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setCreateModal(true)}
          className="flex items-center gap-2 text-white text-sm font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all hover:opacity-90"
          style={{ background: WA_DARK }} data-testid="create-campaign-btn">
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: '#f0fdf4' }}>
            <MessageCircle className="w-7 h-7" style={{ color: WA_GREEN }} />
          </div>
          <p className="font-bold text-slate-700">No campaigns yet</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">Create your first WhatsApp campaign</p>
          <button onClick={() => setCreateModal(true)}
            className="text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all"
            style={{ background: WA_DARK }}>
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition-all" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="font-bold text-slate-900 text-base">{c.name}</h3>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-2.5">
                    <span className="flex items-center gap-1.5 text-xs text-slate-500">
                      <FileText className="w-3.5 h-3.5" /> {c.template_name}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                      <Users className="w-3.5 h-3.5 text-slate-400" /> {(c.total || 0).toLocaleString()} contacts
                    </span>
                    {c.sent_at && (
                      <span className="text-xs text-slate-400">
                        Sent {new Date(c.sent_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>

                  {/* Stats row */}
                  {(c.sent > 0 || c.status === 'sent') && (
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-50">
                      {[
                        { label: 'Sent',      val: c.sent,      color: 'text-blue-600' },
                        { label: 'Delivered', val: c.delivered, color: 'text-emerald-600' },
                        { label: 'Read',      val: c.read,      color: 'text-purple-600' },
                        { label: 'Failed',    val: c.failed,    color: 'text-red-500' },
                      ].map(s => (
                        <div key={s.label} className="text-center min-w-[52px]">
                          <p className={`text-sm font-extrabold ${s.color}`}>{(s.val || 0).toLocaleString()}</p>
                          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {(c.status === 'draft' || c.status === 'scheduled') && (
                    <button onClick={() => handleSend(c)} disabled={sending[c.id]}
                      className="flex items-center gap-1.5 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-all"
                      style={{ background: WA_GREEN }}
                      data-testid={`send-campaign-${c.id}`}>
                      {sending[c.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Send
                    </button>
                  )}
                  {c.status === 'sent' && (
                    <button onClick={() => setDetailModal(c)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 px-3 py-1.5 rounded-xl hover:bg-slate-50 transition-all">
                      <Eye className="w-3.5 h-3.5" /> Details
                    </button>
                  )}
                  <button onClick={() => handleDuplicate(c)} title="Duplicate"
                    className="p-2 rounded-xl text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all">
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(c)} title="Delete"
                    className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100">
          <p className="text-sm text-slate-500">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></p>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 transition-all">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setCurrentPage(p)}
                className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${p === currentPage ? 'text-white shadow-sm' : 'text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                style={p === currentPage ? { background: WA_DARK } : {}}>
                {p}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 transition-all">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" style={{ color: WA_GREEN }} /> New WhatsApp Campaign
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Campaign Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-400"
                placeholder="e.g. Resumption Notice — January" data-testid="campaign-name-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Template *</label>
              <select value={form.template_name} onChange={e => setForm(f => ({ ...f, template_name: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-emerald-400"
                data-testid="campaign-template-select">
                <option value="">Choose template...</option>
                {templates.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
              {templates.length === 0 && (
                <p className="text-xs text-amber-600 mt-1.5">No templates yet — create one in the Templates tab first</p>
              )}
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Language</label>
              <select value={form.template_language} onChange={e => setForm(f => ({ ...f, template_language: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-emerald-400">
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="yo">Yoruba</option>
                <option value="ha">Hausa</option>
                <option value="ig">Igbo</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setCreateModal(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm border border-slate-200 text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={handleCreate}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-all"
                style={{ background: WA_DARK }} data-testid="save-campaign-btn">
                Create Campaign
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!detailModal} onOpenChange={() => setDetailModal(null)}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900">{detailModal?.name}</DialogTitle>
          </DialogHeader>
          {detailModal && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Sent',      val: detailModal.sent,      color: 'text-blue-600',   bg: 'bg-blue-50' },
                  { label: 'Delivered', val: detailModal.delivered, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                  { label: 'Read',      val: detailModal.read,      color: 'text-purple-600',  bg: 'bg-purple-50' },
                  { label: 'Failed',    val: detailModal.failed,    color: 'text-red-500',     bg: 'bg-red-50' },
                ].map(s => (
                  <div key={s.label} className={`${s.bg} rounded-2xl p-4 text-center`}>
                    <p className={`text-2xl font-extrabold ${s.color}`}>{(s.val || 0).toLocaleString()}</p>
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 space-y-1.5">
                <p><span className="font-semibold text-slate-600">Template:</span> {detailModal.template_name}</p>
                <p><span className="font-semibold text-slate-600">Total contacts:</span> {detailModal.total}</p>
                {detailModal.sent_at && (
                  <p><span className="font-semibold text-slate-600">Sent at:</span> {new Date(detailModal.sent_at).toLocaleString()}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  TEMPLATES TAB
// ══════════════════════════════════════════════════════════════════════════════
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [form, setForm] = useState({ name: '', category: 'MARKETING', language: 'en_US', header_text: '', body_text: '', footer_text: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/whatsapp/templates'); setTemplates(r.data || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.body_text.trim()) { toast.error('Name and body are required'); return; }
    try {
      await api.post('/whatsapp/templates', form);
      toast.success('Template created');
      setModal(false);
      setForm({ name: '', category: 'MARKETING', language: 'en_US', header_text: '', body_text: '', footer_text: '' });
      load();
    } catch (err) { toast.error(err?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/whatsapp/templates/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const metaStatusBadge = (s) => {
    const map = {
      APPROVED: 'bg-emerald-100 text-emerald-700',
      PENDING:  'bg-amber-100 text-amber-700',
      REJECTED: 'bg-red-100 text-red-600',
      DRAFT:    'bg-slate-100 text-slate-500',
    };
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${map[s] || map.DRAFT}`}>{s || 'DRAFT'}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Templates</h2>
          <p className="text-sm text-slate-500 mt-0.5">WhatsApp approved message templates</p>
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-2 text-white text-sm font-bold px-4 py-2.5 rounded-xl"
          style={{ background: WA_DARK }} data-testid="create-template-btn">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      : templates.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
          <FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="font-bold text-slate-600">No templates yet</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">Templates must be approved by Meta before use in campaigns</p>
          <button onClick={() => setModal(true)} className="text-white text-sm font-semibold px-5 py-2.5 rounded-xl" style={{ background: WA_DARK }}>
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(t => (
            <div key={t.id} className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-sm transition-all" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <h3 className="font-bold text-slate-800 text-sm">{t.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded-full">{t.category}</span>
                    {metaStatusBadge(t.meta_status)}
                  </div>
                </div>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {/* WhatsApp bubble preview */}
              <div className="rounded-2xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed"
                style={{ background: WA_LIGHT, maxWidth: '90%' }}>
                {t.header_text && <p className="font-bold text-xs mb-1 text-slate-600">{t.header_text}</p>}
                <p className="text-sm">{t.body_text}</p>
                {t.footer_text && <p className="text-[11px] text-slate-500 mt-1">{t.footer_text}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Template Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900">Create Message Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Template Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
                  placeholder="school_notice" data-testid="template-name-input" />
                <p className="text-[10px] text-slate-400 mt-1">lowercase, underscores only</p>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white outline-none focus:border-emerald-400">
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                  <option value="AUTHENTICATION">Authentication</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Header (optional)</label>
              <input value={form.header_text} onChange={e => setForm(f => ({ ...f, header_text: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
                placeholder="EDUVA School Notice" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Body *</label>
              <textarea value={form.body_text} onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400 resize-none h-28"
                placeholder="Dear Parent, this is to inform you that school resumes on Monday..."
                data-testid="template-body-input" />
              <p className="text-[10px] text-slate-400 mt-1">Use {'{{1}}'}, {'{{2}}'} for variable placeholders (e.g., parent name, date)</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Footer (optional)</label>
              <input value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
                placeholder="EDUVA School Management" />
            </div>
            {/* Live preview */}
            {form.body_text && (
              <div className="bg-slate-50 rounded-2xl p-3.5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Preview</p>
                <div className="rounded-2xl rounded-tl-sm p-3 inline-block max-w-[80%]" style={{ background: WA_LIGHT }}>
                  {form.header_text && <p className="font-bold text-xs mb-1 text-slate-700">{form.header_text}</p>}
                  <p className="text-sm text-slate-800">{form.body_text}</p>
                  {form.footer_text && <p className="text-[11px] text-slate-500 mt-1">{form.footer_text}</p>}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white"
                style={{ background: WA_DARK }} data-testid="save-template-btn">
                Save Template
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CONTACTS TAB
// ══════════════════════════════════════════════════════════════════════════════
function ContactsTab() {
  const [contacts, setContacts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]         = useState(0);
  const [modal, setModal]         = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkText, setBulkText]   = useState('');
  const [form, setForm] = useState({ phone: '', name: '', email: '', opted_in: true });
  const [toggling, setToggling]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/whatsapp/contacts?page=${currentPage}&limit=20&search=${encodeURIComponent(search)}`);
      setContacts(r.data || []);
      setTotalPages(r.total_pages || 1);
      setTotal(r.total || 0);
    } finally { setLoading(false); }
  }, [currentPage, search]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.phone.trim()) { toast.error('Phone number is required'); return; }
    try {
      await api.post('/whatsapp/contacts', form);
      toast.success('Contact added');
      setModal(false);
      setForm({ phone: '', name: '', email: '', opted_in: true });
      load();
    } catch (err) { toast.error(err?.detail || 'Failed to add'); }
  };

  const handleBulkImport = async () => {
    const lines = bulkText.trim().split('\n').filter(Boolean);
    const contacts = lines.map(line => {
      const [phone, name, email] = line.split(',').map(s => s.trim());
      return { phone, name: name || '', email: email || '', opted_in: true };
    }).filter(c => c.phone);
    if (contacts.length === 0) { toast.error('No valid contacts found'); return; }
    try {
      const r = await api.post('/whatsapp/contacts/import', { contacts });
      toast.success(`Imported ${r.added} contacts (${r.skipped} skipped)`);
      setBulkModal(false); setBulkText(''); load();
    } catch { toast.error('Import failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/whatsapp/contacts/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const toggleOptIn = async (c) => {
    setToggling(t => ({ ...t, [c.id]: true }));
    try {
      await api.put(`/whatsapp/contacts/${c.id}`, { ...c, opted_in: !c.opted_in });
      toast.success(c.opted_in ? 'Opted out' : 'Opted in');
      load();
    } catch { toast.error('Failed to update'); }
    finally { setToggling(t => ({ ...t, [c.id]: false })); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Contacts</h2>
          <p className="text-sm text-slate-500 mt-0.5">{total} contact{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              className="outline-none text-sm w-40" placeholder="Search..." data-testid="contacts-search" />
          </div>
          <button onClick={() => setBulkModal(true)}
            className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-slate-50 transition-all">
            <Upload className="w-4 h-4" /> Import
          </button>
          <button onClick={() => setModal(true)}
            className="flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-xl"
            style={{ background: WA_DARK }} data-testid="add-contact-btn">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
        : contacts.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="font-bold text-slate-600">No contacts yet</p>
            <p className="text-sm text-slate-400 mt-1">Add contacts individually or import in bulk</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['Contact', 'Phone', 'Opt-in', 'Source', 'Added', ''].map(h => (
                  <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {contacts.map(c => (
                <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`contact-${c.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: WA_DARK }}>
                        {(c.name || c.phone).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{c.name || '—'}</p>
                        {c.email && <p className="text-xs text-slate-400">{c.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 font-mono text-sm text-slate-600">{c.phone}</td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => toggleOptIn(c)} disabled={toggling[c.id]} className="transition-all">
                      {c.opted_in
                        ? <span className="flex items-center gap-1 text-xs text-emerald-600 font-bold">
                            <CheckCircle className="w-4 h-4" /> Opted In
                          </span>
                        : <span className="flex items-center gap-1 text-xs text-slate-400">
                            <XCircle className="w-4 h-4" /> Opted Out
                          </span>}
                    </button>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-slate-500 capitalize">{c.source || 'manual'}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—'}</td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => handleDelete(c.id)}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500">Page {currentPage} of {totalPages}</p>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add contact modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900">Add Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Phone (WhatsApp) *</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-400 font-mono"
                placeholder="+2348012345678" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-400"
                placeholder="Full name" data-testid="contact-name-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-400"
                placeholder="optional" />
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setForm(f => ({ ...f, opted_in: !f.opted_in }))}>
                {form.opted_in
                  ? <ToggleRight className="w-8 h-8 text-emerald-500" />
                  : <ToggleLeft className="w-8 h-8 text-slate-300" />}
              </button>
              <span className="text-sm font-semibold text-slate-700">Opted in to WhatsApp messages</span>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white" style={{ background: WA_DARK }}
                data-testid="save-contact-btn">Save Contact</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk import modal */}
      <Dialog open={bulkModal} onOpenChange={setBulkModal}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900">Bulk Import Contacts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600">
              <p className="font-bold mb-1">Format: one contact per line</p>
              <code className="text-slate-500">+2348012345678, John Doe, john@email.com</code>
              <br /><code className="text-slate-500">+2348087654321, Jane Smith</code>
              <p className="mt-1 text-slate-400">Name and email are optional. Phone number is required.</p>
            </div>
            <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-emerald-400 resize-none h-40"
              placeholder="+2348012345678, John Doe&#10;+2348087654321, Jane Smith" />
            <div className="flex gap-3">
              <button onClick={() => setBulkModal(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleBulkImport}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white" style={{ background: WA_DARK }}>
                Import
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ANALYTICS TAB
// ══════════════════════════════════════════════════════════════════════════════
function AnalyticsTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/whatsapp/analytics'); setData(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>;
  if (!data) return null;

  const sent      = data.messages.sent      || 0;
  const delivered = data.messages.delivered || 0;
  const read      = data.messages.read      || 0;
  const failed    = data.messages.failed    || 0;
  const delivRate = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : '—';
  const readRate  = sent > 0 ? ((read / sent) * 100).toFixed(1) : '—';

  const kpis = [
    { label: 'Total Contacts', val: (data.contacts.total || 0).toLocaleString(),  sub: `${data.contacts.opted_in} opted in`,  color: 'text-slate-800',   icon: Users,          bg: 'bg-slate-50' },
    { label: 'Campaigns Sent', val: (data.campaigns.sent || 0).toLocaleString(),  sub: `of ${data.campaigns.total} total`,     color: 'text-emerald-700', icon: Send,           bg: 'bg-emerald-50' },
    { label: 'Messages Sent',  val: sent.toLocaleString(),                          sub: `${failed} failed`,                    color: 'text-blue-700',    icon: MessageCircle,  bg: 'bg-blue-50' },
    { label: 'Delivery Rate',  val: `${delivRate}%`,                                sub: `${delivered.toLocaleString()} delivered`, color: 'text-purple-700', icon: CheckCircle, bg: 'bg-purple-50' },
    { label: 'Read Rate',      val: `${readRate}%`,                                 sub: `${read.toLocaleString()} messages read`,  color: 'text-amber-700',  icon: Eye,         bg: 'bg-amber-50' },
  ];

  return (
    <div className="space-y-8">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map(k => (
          <div key={k.label} className={`${k.bg} rounded-2xl p-4`}>
            <k.icon className={`w-5 h-5 mb-2 ${k.color} opacity-70`} />
            <p className={`text-2xl font-extrabold ${k.color}`}>{k.val}</p>
            <p className="text-xs font-semibold text-slate-500 mt-0.5">{k.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Campaign Performance Table */}
      {data.recent_campaigns.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-50">
            <h3 className="font-bold text-slate-900">Campaign Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Campaign', 'Status', 'Total', 'Sent', 'Delivered', 'Read', 'Failed', 'Delivery Rate'].map(h => (
                    <th key={h} className="text-left px-5 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.recent_campaigns.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50/80" data-testid={`analytics-wa-row-${c.id}`}>
                    <td className="px-5 py-3.5 font-semibold text-slate-800 max-w-44 truncate">{c.name}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-3.5 text-slate-600">{(c.total || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-blue-600 font-bold">{(c.sent || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-emerald-600 font-bold">{(c.delivered || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-purple-600 font-bold">{(c.read || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-red-500">{(c.failed || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 font-semibold text-slate-600">
                      {c.sent > 0 ? `${((c.delivered / c.sent) * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
